import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  TrendingUp, 
  BarChart3,
  Building2,
  Grid3X3,
  Image as ImageIcon
} from 'lucide-react';
import { useApprovalStats } from '@/features/approval/hooks/useApprovalStats';
import type { ApprovalStats as ApprovalStatsType } from '../../types';

interface ApprovalStatsProps {
  initialData?: ApprovalStatsType;
}

const ApprovalStats: React.FC<ApprovalStatsProps> = ({ initialData }) => {
  const { data: fetchedStats, isLoading, error } = useApprovalStats();
  
  // Use initialData if provided (e.g. for testing or parent passing data), otherwise use fetched data
  const stats = initialData || fetchedStats;

  if (isLoading && !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if ((error && !stats) || !stats) {
    return (
      <Card className="col-span-full">
        <CardContent className="p-6 text-center text-destructive">
          Failed to load approval statistics
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-amber-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Total Pending</p>
                <p className="text-2xl font-bold">{stats.total_pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Grid3X3 className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Galleries</p>
                <p className="text-2xl font-bold">{stats.pending_galleries}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <ImageIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Images</p>
                <p className="text-2xl font-bold">{stats.pending_images}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Total Approved</p>
                <p className="text-2xl font-bold">{stats.approved_today}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.rejected_today} rejected
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Region */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              By Region
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(stats.by_region).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.by_region)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([region, count]) => (
                    <div key={region} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">
                        {region.replace('_', ' ')}
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No regional data available
              </p>
            )}
          </CardContent>
        </Card>

        {/* By Service */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              By Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(stats.by_service).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.by_service)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([service, count]) => (
                    <div key={service} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">
                        {service.replace('_', ' ')}
                      </span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No service data available
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApprovalStats;
